import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, Play, Handshake, ShieldCheck, Settings, ChevronRight, ClipboardList, UserCheck, Lock } from "lucide-react";
import { API, qs, useTaxonomy } from "../lib/api";
import { catMeta } from "../lib/media";

const TRUST = [
  { icon: Handshake, title: "Quality Contractors", sub: "Approved by Verge operations" },
  { icon: ShieldCheck, title: "Your Information Stays Private", sub: "We connect you, you choose" },
  { icon: Settings, title: "Fair & Equal Opportunities", sub: "All contractors get a chance" },
];

const STEPS = [
  { icon: ClipboardList, title: "Describe the job", body: "Pick a trade and area. Your contact details stay sealed in your request." },
  { icon: Search, title: "Compare anonymous matches", body: "See approved providers by trade and experience — names hidden, ranked fairly, never by ad spend." },
  { icon: UserCheck, title: "Engage the one you like", body: "Engaging reveals who the vendor is to you only. You can still pick another." },
  { icon: Lock, title: "Approve what to share", body: "Choose the exact fields and purpose. Only that vendor sees them, for 7 days." },
];

function ServicePanel() {
  const { categories } = useTaxonomy();
  return (
    <div className="relative hidden lg:block" data-testid="hero-service-panel">
      <div className="svc-slash" aria-hidden />
      <ul className="svc-panel space-y-3">
        {categories.map((c, i) => {
          const m = catMeta(c.id);
          return (
            <li key={c.id} style={{ marginLeft: `${(categories.length - i) * 14}px` }}>
              <Link to={`/directory?category=${c.id}`} className="group flex items-center gap-4" data-testid={`hero-category-${c.id}`}>
                <span className="grid h-12 w-12 place-items-center rounded-md bg-[#2a7bff] text-white shadow-lg ring-1 ring-white/30 transition-transform group-hover:scale-105"><m.icon className="h-6 w-6" aria-hidden /></span>
                <span><span className="block text-lg font-bold text-white">{c.name}</span><span className="text-sm text-[#d7e3ff]">{m.blurb}</span></span>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function MobileCategories() {
  const { categories } = useTaxonomy();
  return (
    <ul className="mt-6 divide-y divide-[var(--steel-2)] overflow-hidden rounded-lg bg-white lg:hidden" data-testid="hero-mobile-categories">
      {categories.map((c) => {
        const m = catMeta(c.id);
        return (
          <li key={c.id}>
            <Link to={`/directory?category=${c.id}`} className="flex items-center gap-3 px-4 py-3 font-semibold text-[var(--ink)]" data-testid={`hero-mobile-category-${c.id}`}>
              <m.icon className="h-6 w-6 text-[var(--blue)]" aria-hidden /><span className="flex-1">{c.name}</span><ChevronRight className="h-4 w-4" aria-hidden />
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

function HeroSearch() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [hint, setHint] = useState<any[] | null>(null);
  useEffect(() => {
    if (q.trim().length < 2) { setHint(null); return; }
    const t = setTimeout(() => {
      axios.get(`${API}/public/suggest`, { params: { q } }).then((r) => setHint(r.data.categories)).catch(() => setHint(null));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  const go = (e: React.FormEvent) => { e.preventDefault(); nav(`/directory?${qs({ q: q.trim() })}`); };
  const post = `/customer/new?${qs({ q: q.trim(), category: hint?.length === 1 ? hint[0].id : "" })}`;
  return (
    <div className="mt-8 max-w-xl">
      <form onSubmit={go} role="search" className="flex flex-col gap-2 rounded-lg bg-white p-2 shadow-xl sm:flex-row">
        <label className="flex flex-1 items-center gap-2 px-2"><Search className="h-5 w-5 text-[var(--muted)]" aria-hidden /><span className="sr-only">What do you need help with?</span>
          <input className="w-full py-3 text-[var(--ink)] outline-none" placeholder="What do you need? e.g. leaky faucet" value={q} onChange={(e) => setQ(e.target.value)} maxLength={80} data-testid="home-search-input" />
        </label>
        <button className="btn btn-red btn-lg" data-testid="home-find-service-button">Find a Service</button>
      </form>
      <p className="mt-2 min-h-[1.25rem] text-sm text-[#dbe3f3]" aria-live="polite" data-testid="home-search-hint">
        {hint && (hint.length ? <>Looks like: <strong className="text-white">{hint.map((c) => c.name).join(", ")}</strong></> : "No trade matched yet — try different words or browse all services.")}
      </p>
      <div className="mt-3 flex flex-col gap-3 sm:flex-row">
        <Link to={post} className="btn btn-primary btn-lg" data-testid="home-post-job-button"><ClipboardList className="h-5 w-5" aria-hidden />Post a job</Link>
        <a href="#how" className="btn btn-outline-light btn-lg" data-testid="home-how-it-works-button"><Play className="h-5 w-5" aria-hidden />How It Works</a>
      </div>
    </div>
  );
}

export default function Home() {
  return (
    <div>
      <section className="hero" style={{ backgroundImage: "url(/img/hero.jpg)" }}>
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 pb-12 pt-10 lg:grid-cols-[1.15fr_1fr] lg:pb-20 lg:pt-16">
          <div className="fade-up">
            <h1 className="hero-title text-white">Find Trusted<br />Local <span className="text-[#2f7bff]">Services</span></h1>
            <p className="mt-3 text-xl font-bold text-white sm:text-3xl">Home. Business. Projects. Done Right.</p>
            <p className="mt-5 max-w-xl text-base text-[#dbe3f3] md:text-lg">
              Verge Services connects you with approved local contractors for any project. Compare options, choose with confidence, and share your details only when you decide.
            </p>
            <HeroSearch />
            <MobileCategories />
          </div>
          <ServicePanel />
        </div>
        <div className="trust-band relative">
          <div className="mx-auto grid max-w-7xl gap-6 px-4 py-7 sm:grid-cols-3">
            {TRUST.map((t, i) => (
              <div key={t.title} className="flex items-center gap-5" data-testid={`trust-item-${i}`}>
                <span className="trust-icon"><t.icon className="h-7 w-7" aria-hidden /></span>
                <span><span className="block text-lg font-bold text-[var(--navy)]">{t.title}</span><span className="text-sm text-[#3b4660]">{t.sub}</span></span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section id="how" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-16">
        <h2 className="text-3xl font-extrabold text-[var(--navy)] sm:text-4xl">How It Works</h2>
        <ol className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s, i) => (
            <li key={s.title} className="card fade-up" style={{ animationDelay: `${i * 70}ms` }} data-testid={`how-step-${i}`}>
              <span className="grid h-11 w-11 place-items-center rounded-md bg-[var(--blue)] text-white"><s.icon className="h-5 w-5" aria-hidden /></span>
              <h3 className="mt-3 text-xl font-bold">{i + 1}. {s.title}</h3>
              <p className="mt-1 text-sm text-[var(--muted)]">{s.body}</p>
            </li>
          ))}
        </ol>
        <p className="mt-8 rounded-md border-l-4 border-[var(--red)] bg-white p-4 text-sm text-[var(--muted)]" data-testid="home-preview-notice">
          Closed preview: accounts are by invitation from Verge operations. Vendor organizations appear only after approval.
        </p>
      </section>
    </div>
  );
}
